import React, { useState, useEffect } from 'react';
import { Mail, Clock, Send, Check, MapPin, AlertCircle } from 'lucide-react';
import SEO from '../components/SEO';

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  // Math Captcha state
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0 });
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaError, setCaptchaError] = useState('');

  const generateCaptcha = () => {
    setCaptcha({
      num1: Math.floor(Math.random() * 10) + 1,
      num2: Math.floor(Math.random() * 10) + 1
    });
    setCaptchaAnswer('');
    setCaptchaError('');
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Captcha Validation
    if (parseInt(captchaAnswer) !== captcha.num1 + captcha.num2) {
      setCaptchaError('Răspuns incorect. Încearcă din nou.');
      generateCaptcha();
      return;
    }

    setSending(true);
    await new Promise(r => setTimeout(r, 1000));
    setSubmitted(true);
    setSending(false);
  };

  return (
    <main className="min-h-screen bg-[#0a0a0a] pt-16 relative overflow-hidden" data-testid="contact-page">
      <SEO
        title="Contactează-ne"
        url="https://seltz.eu/contact"
        description="Ai o întrebare, o propunere sau vrei să ne spui cât de mult îți place Seltz? Suntem aici. Trimite-ne un email și vorbim."
      />
      {/* Background Image & Overlay */}
      <div
        className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat opacity-40 mix-blend-overlay"
        style={{ backgroundImage: 'url(/assets/images/hero-bg.jpg)' }}
      />
      <div className="fixed inset-0 z-0 bg-gradient-to-b from-[#0a0a0a]/80 via-[#0a0a0a]/95 to-[#0a0a0a]" />

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 lg:py-32">
        <div className="text-center mb-16 lg:mb-24">
          <h1
            className="text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-200 to-gray-400 font-black italic leading-tight mb-6"
            style={{ fontFamily: 'Nunito, sans-serif', fontSize: 'clamp(3rem, 8vw, 6rem)' }}
          >
            hai să vorbim
          </h1>
          <p className="text-white/70 text-lg sm:text-xl max-w-2xl mx-auto" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Ai o întrebare, o propunere sau pur și simplu vrei să ne spui cât de mult îți place Seltz? Suntem aici.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">

          {/* Contact Information (Left Column) */}
          <div className="lg:col-span-4 flex flex-col gap-6">
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition-colors group">
              <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Mail size={24} className="text-white" />
              </div>
              <h3 className="text-white font-black italic text-xl mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>Trimite-ne un email</h3>
              <p className="text-white/60 mb-2">Răspundem rapid, promitem.</p>
              <a href="mailto:drink@seltz.eu" className="text-white font-bold text-lg hover:text-orange-400 transition-colors">drink@seltz.eu</a>
            </div>

            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition-colors group">
              <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Clock size={24} className="text-white" />
              </div>
              <h3 className="text-white font-black italic text-xl mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>Program</h3>
              <p className="text-white/60 mb-1">Luni - Vineri: 9:00 - 18:00</p>
              <p className="text-white/60">Sâmbătă: 10:00 - 14:00</p>
            </div>

            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition-colors group">
              <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <MapPin size={24} className="text-white" />
              </div>
              <h3 className="text-white font-black italic text-xl mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>Sediu central</h3>
              <p className="text-white/60 mb-1">București, România</p>
              <p className="text-white/60">Sector 1</p>
            </div>
          </div>

          {/* Contact Form (Right Column) */}
          <div className="lg:col-span-8">
            <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-[2rem] p-8 sm:p-12 shadow-2xl">
              {submitted ? (
                <div className="text-center py-20">
                  <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check size={40} className="text-green-400" />
                  </div>
                  <h3 className="text-white font-black italic text-3xl mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
                    mesaj primit!
                  </h3>
                  <p className="text-white/60 text-lg" style={{ fontFamily: 'Nunito, sans-serif' }}>
                    Mulțumim că ne-ai scris. Un membru al echipei noastre va reveni cu un răspuns către tine în curând.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-6 sm:gap-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
                    <div className="flex flex-col gap-2">
                      <label className="text-white/80 font-bold text-xs uppercase tracking-widest pl-2" style={{ fontFamily: 'Nunito, sans-serif' }}>Numele tău</label>
                      <input
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Ex: Ana Popescu"
                        required
                        className="bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder-white/30 focus:outline-none focus:border-white/40 focus:bg-white/10 transition-all"
                        style={{ fontFamily: 'Nunito, sans-serif' }}
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-white/80 font-bold text-xs uppercase tracking-widest pl-2" style={{ fontFamily: 'Nunito, sans-serif' }}>Adresa de email</label>
                      <input
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="hello@exemplu.ro"
                        required
                        className="bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder-white/30 focus:outline-none focus:border-white/40 focus:bg-white/10 transition-all"
                        style={{ fontFamily: 'Nunito, sans-serif' }}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-white/80 font-bold text-xs uppercase tracking-widest pl-2" style={{ fontFamily: 'Nunito, sans-serif' }}>Subiect</label>
                    <input
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      placeholder="Despre ce dorești să vorbim?"
                      required
                      className="bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder-white/30 focus:outline-none focus:border-white/40 focus:bg-white/10 transition-all"
                      style={{ fontFamily: 'Nunito, sans-serif' }}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-white/80 font-bold text-xs uppercase tracking-widest pl-2" style={{ fontFamily: 'Nunito, sans-serif' }}>Mesajul tău</label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Scrie-ne tot ce ai pe suflet..."
                      rows={6}
                      required
                      className="bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder-white/30 focus:outline-none focus:border-white/40 focus:bg-white/10 transition-all resize-none"
                      style={{ fontFamily: 'Nunito, sans-serif' }}
                    />
                  </div>

                  {/* Math Captcha */}
                  <div className="flex flex-col gap-2">
                    <label className="text-white/80 font-bold text-xs uppercase tracking-widest pl-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                      Verificare de securitate: Cât fac {captcha.num1} + {captcha.num2}?
                    </label>
                    <input
                      type="number"
                      value={captchaAnswer}
                      onChange={(e) => { setCaptchaAnswer(e.target.value); setCaptchaError(''); }}
                      placeholder="Raspunsul tau..."
                      required
                      className={`bg-white/5 border ${captchaError ? 'border-red-500' : 'border-white/10'} rounded-2xl px-6 py-4 text-white placeholder-white/30 focus:outline-none focus:border-white/40 focus:bg-white/10 transition-all`}
                      style={{ fontFamily: 'Nunito, sans-serif' }}
                    />
                    {captchaError && (
                      <div className="flex items-center gap-1.5 mt-1 text-red-400 text-[11px] font-bold pl-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                        <AlertCircle size={14} /> {captchaError}
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={sending}
                    className="mt-4 flex items-center justify-center gap-3 bg-white text-[#0a0a0a] font-black italic text-lg px-8 py-5 rounded-2xl hover:scale-[1.02] active:scale-[0.98] transition-transform disabled:opacity-50 disabled:hover:scale-100"
                    style={{ fontFamily: 'Nunito, sans-serif' }}
                  >
                    {sending ? (
                      <span className="flex items-center gap-2">Se trimite...</span>
                    ) : (
                      <span className="flex items-center gap-2">Trimite mesajul <Send size={18} /></span>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

        </div>
      </div>
    </main>
  );
};

export default ContactPage;
